import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card, Tabs, Button, Input, Timeline, Form, Modal, Row, Col, Select, DatePicker, Alert, Upload,
} from "antd";
import { DownloadOutlined, EyeOutlined, DeleteOutlined, EditOutlined, CheckOutlined, CloseOutlined, FileOutlined, UploadOutlined } from "@ant-design/icons";
import moment from "moment";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import { Severty, ShowToast } from "../../helper/toast";

const { TextArea } = Input;

// Post Body/Graphic Brief/Design Instructions are plain text, so a pasted
// URL survives editing fine but rendered straight as {value} it's just
// inert text — no different from any other word in the paragraph. This
// turns any http(s)/www URL substring into a real clickable link without
// touching the underlying plain-text value or the edit form.
const URL_PATTERN = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
function linkifyText(text) {
  if (!text) return text;
  return text.split(URL_PATTERN).flatMap((part, i) => {
    if (!/^(https?:\/\/|www\.)/.test(part)) return [part];
    // Strip trailing sentence punctuation ('post.', 'link).') from the URL
    // itself so it doesn't get swallowed into the href, but keep it visible
    // as plain text right after the link.
    const trailingMatch = part.match(/[.,;:!?)\]]+$/);
    const trailing = trailingMatch ? trailingMatch[0] : "";
    const url = trailing ? part.slice(0, -trailing.length) : part;
    return [
      <a key={`${i}-link`} href={url.startsWith("http") ? url : `https://${url}`} target="_blank" rel="noreferrer">
        {url}
      </a>,
      trailing,
    ];
  });
}

// Same friendly mapping as the Graphics portal's MyRequests "Stage" column,
// so the hero pill reads the same progress language everywhere the request
// is viewed. "Pending"/"Assigned" both just mean nobody's started yet;
// the backend auto-flips them to "In Progress" the moment the assigned
// GM opens the request (GraphicsService.markOpenedByAssignee).
const STAGE_LABEL = {
  Pending: "New",
  Assigned: "New",
  "In Progress": "In Progress",
  "Waiting for Review": "Under Review",
  "Revision Requested": "Change Requested",
  Revised: "Revised",
  Approved: "Approved",
  Completed: "Completed",
  Overdue: "Overdue",
  Cancelled: "Cancelled",
};

function GraphicsRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { request } = useRequest();
  const [details, setDetails] = useState(null);
  const [versions, setVersions] = useState([]);
  const [comments, setComments] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [decisionModal, setDecisionModal] = useState(null); // "approved" | "revision_requested" | null
  const [decisionForm] = Form.useForm();
  const [deciding, setDeciding] = useState(false);
  const [commentSaving, setCommentSaving] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [editSaving, setEditSaving] = useState(false);
  const [editFileList, setEditFileList] = useState([]);
  const [members, setMembers] = useState([]);
  const [editingOriginalAssignee, setEditingOriginalAssignee] = useState(null);
  const assignedToWatch = Form.useWatch("assignedTo", editForm);
  const [extensionDecisionModal, setExtensionDecisionModal] = useState(null); // "approved" | "rejected" | null
  const [extensionDecisionForm] = Form.useForm();
  const [extensionDeciding, setExtensionDeciding] = useState(false);
  const [deletingVersionId, setDeletingVersionId] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);

  const fetchAll = () => {
    request({
      url: apiPath.graphicsRequest(id),
      method: "GET",
      onSuccess: (data) => setDetails(data?.data?.request || null),
      onError: () => {},
    });
    request({
      url: apiPath.graphicsRequestVersions(id),
      method: "GET",
      onSuccess: (data) => setVersions(data?.data?.versions || []),
      onError: () => {},
    });
    request({
      url: apiPath.graphicsRequestComments(id),
      method: "GET",
      onSuccess: (data) => setComments(data?.data?.comments || []),
      onError: () => {},
    });
    request({
      url: apiPath.graphicsRequestAuditLog(id),
      method: "GET",
      onSuccess: (data) => setAuditLog(data?.data?.auditLog || []),
      onError: () => {},
    });
  };

  useEffect(() => {
    fetchAll();
    request({
      url: apiPath.graphicsTeamMembers,
      method: "GET",
      onSuccess: (data) => setMembers(data?.data?.members || []),
      onError: () => {},
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const addComment = () => {
    if (!commentText.trim()) return;
    setCommentSaving(true);
    request({
      url: apiPath.graphicsRequestComments(id),
      method: "POST",
      data: { message: commentText },
      onSuccess: () => {
        setCommentSaving(false);
        setCommentText("");
        fetchAll();
      },
      onError: () => {
        setCommentSaving(false);
        ShowToast("Failed to add comment", Severty.ERROR);
      },
    });
  };

  const sendDecision = (decision, comment) => {
    setDeciding(true);
    request({
      url: apiPath.graphicsRequestReviewDecision(id),
      method: "PUT",
      data: { decision, comment },
      onSuccess: (data) => {
        setDeciding(false);
        if (data.status === 200) {
          ShowToast(
            decision === "approved"
              ? "Submission approved"
              : details.status === "Completed"
                ? "Request reopened"
                : "Revisions requested",
            Severty.SUCCESS
          );
          setDecisionModal(null);
          decisionForm.resetFields();
          fetchAll();
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: () => {
        setDeciding(false);
        ShowToast("Failed to record decision", Severty.ERROR);
      },
    });
  };

  // Approving needs no feedback — only revision requests open the comment modal.
  const submitDecision = (values) => sendDecision("revision_requested", values.comment);

  const deleteRequest = () => {
    Modal.confirm({
      title: "Delete this graphics request?",
      content: `"${details.campaignProject}" will be removed and taken off the Graphics member's queue. This can't be undone.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: () =>
        new Promise((resolve, reject) => {
          request({
            url: apiPath.graphicsRequest(id),
            method: "DELETE",
            onSuccess: (data) => {
              if (data.status === 200) {
                ShowToast("Graphics request deleted", Severty.SUCCESS);
                navigate("/workload");
                resolve();
              } else {
                ShowToast(data.message, Severty.ERROR);
                reject();
              }
            },
            onError: (err) => {
              ShowToast(err?.response?.data?.message || "Failed to delete request", Severty.ERROR);
              reject();
            },
          });
        }),
    });
  };

  const openEdit = () => {
    setEditingOriginalAssignee(details.assignedTo?._id || null);
    editForm.setFieldsValue({
      clientNameRef: details.clientNameRef,
      campaignProject: details.campaignProject,
      postTitle: details.postTitle,
      postBody: details.postBody,
      graphicBrief: details.graphicBrief,
      designInstructions: details.designInstructions,
      deliveryDate: details.deliveryDate ? moment(details.deliveryDate) : null,
      priority: details.priority,
      assignedTo: details.assignedTo?._id || undefined,
      reassignReason: undefined,
    });
    setEditFileList([]);
    setEditVisible(true);
  };

  const finishEditDetails = (values) => {
    const payload = { ...values };
    delete payload.assignedTo;
    delete payload.reassignReason;
    if (payload.deliveryDate) {
      payload.deliveryDate = `${payload.deliveryDate.format("YYYY-MM-DD")}T00:00:00.000Z`;
    }
    // The details endpoint now also accepts new reference files, so it has
    // to be multipart the same way create's POST is — a plain JSON body
    // would hang on the server's formidable parser.
    const formData = new FormData();
    Object.entries(payload).forEach(([key, val]) => {
      if (val !== undefined && val !== null) formData.append(key, val);
    });
    editFileList.forEach((f) => formData.append("referenceFiles", f));
    request({
      url: apiPath.graphicsRequestUpdate(id),
      method: "PUT",
      data: formData,
      onSuccess: (data) => {
        setEditSaving(false);
        if (data.status === 200) {
          ShowToast("Graphics request updated successfully", Severty.SUCCESS);
          setEditVisible(false);
          setEditingOriginalAssignee(null);
          setEditFileList([]);
          fetchAll();
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: (err) => {
        setEditSaving(false);
        ShowToast(err?.response?.data?.message || "Failed to update request", Severty.ERROR);
      },
    });
  };

  // Admin always overrides the workload block (per spec), so reassignment
  // here just needs the short-reason gate, no blocked-modal detour.
  const submitEdit = (values) => {
    setEditSaving(true);
    const reassigning = values.assignedTo !== editingOriginalAssignee;
    if (!reassigning) return finishEditDetails(values);

    request({
      url: apiPath.graphicsOverrideAssign(id),
      method: "PUT",
      data: { assignedTo: values.assignedTo, reason: values.reassignReason, overrideReason: values.reassignReason },
      onSuccess: (data) => {
        if (data.status === 200) {
          finishEditDetails(values);
        } else {
          setEditSaving(false);
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: (err) => {
        setEditSaving(false);
        ShowToast(err?.response?.data?.message || "Failed to reassign request", Severty.ERROR);
      },
    });
  };

  const submitExtensionDecision = (values) => {
    setExtensionDeciding(true);
    request({
      url: apiPath.graphicsRequestExtensionDecision(id),
      method: "PUT",
      data: {
        decision: extensionDecisionModal,
        approvedDate: values.approvedDate ? `${values.approvedDate.format("YYYY-MM-DD")}T00:00:00.000Z` : undefined,
        note: values.note,
      },
      onSuccess: (data) => {
        setExtensionDeciding(false);
        if (data.status === 200) {
          ShowToast(
            extensionDecisionModal === "approved" ? "Extension approved" : "Extension rejected",
            Severty.SUCCESS
          );
          setExtensionDecisionModal(null);
          extensionDecisionForm.resetFields();
          fetchAll();
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: (err) => {
        setExtensionDeciding(false);
        ShowToast(err?.response?.data?.message || "Failed to record decision", Severty.ERROR);
      },
    });
  };

  const deleteVersion = (version) => {
    Modal.confirm({
      title: `Delete Version ${version.version}?`,
      content: "This file will be permanently removed from the request. This can't be undone.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: () =>
        new Promise((resolve, reject) => {
          setDeletingVersionId(version._id);
          request({
            url: apiPath.graphicsRequestDeleteVersion(id, version._id),
            method: "DELETE",
            onSuccess: (data) => {
              setDeletingVersionId(null);
              if (data.status === 200) {
                ShowToast("Version deleted", Severty.SUCCESS);
                fetchAll();
                resolve();
              } else {
                ShowToast(data.message, Severty.ERROR);
                reject();
              }
            },
            onError: (err) => {
              setDeletingVersionId(null);
              ShowToast(err?.response?.data?.message || "Failed to delete version", Severty.ERROR);
              reject();
            },
          });
        }),
    });
  };

  // The browser's `download` attribute on an <a> is silently ignored for
  // cross-origin URLs (our files live on S3, a different origin from this
  // app) — clicking just opened/navigated to the file instead of saving it.
  // Fetching it as a blob and downloading from a same-origin blob: URL
  // makes the filename stick regardless of where the file actually lives.
  const downloadFile = async (url, name) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = name || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      window.open(url, "_blank");
    }
  };

  const isImageFile = (name) => /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name || "");
  const isPdfFile = (name) => /\.pdf$/i.test(name || "");

  const [decidingDeletionId, setDecidingDeletionId] = useState(null);
  const [previewVersion, setPreviewVersion] = useState(null);
  const [brokenThumbs, setBrokenThumbs] = useState({});

  const decideVersionDeletion = (version, decision) => {
    setDecidingDeletionId(version._id);
    request({
      url: apiPath.graphicsRequestVersionDeletionDecision(id, version._id),
      method: "PUT",
      data: { decision },
      onSuccess: (data) => {
        setDecidingDeletionId(null);
        if (data.status === 200) {
          ShowToast(decision === "approved" ? "Deletion approved" : "Deletion rejected", Severty.SUCCESS);
          fetchAll();
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: (err) => {
        setDecidingDeletionId(null);
        ShowToast(err?.response?.data?.message || "Failed to record decision", Severty.ERROR);
      },
    });
  };

  const startEditingNote = (version) => {
    setEditingNoteId(version._id);
    setNoteDraft(version.notes || "");
  };

  const cancelEditingNote = () => {
    setEditingNoteId(null);
    setNoteDraft("");
  };

  const saveNote = (version) => {
    setNoteSaving(true);
    request({
      url: apiPath.graphicsRequestUpdateVersionNotes(id, version._id),
      method: "PUT",
      data: { notes: noteDraft },
      onSuccess: (data) => {
        setNoteSaving(false);
        if (data.status === 200) {
          ShowToast("Note saved", Severty.SUCCESS);
          setEditingNoteId(null);
          setNoteDraft("");
          fetchAll();
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: (err) => {
        setNoteSaving(false);
        ShowToast(err?.response?.data?.message || "Failed to save note", Severty.ERROR);
      },
    });
  };

  if (!details) {
    return <Card loading bordered={false} />;
  }

  const isOverdue = details.status === "Overdue";
  const metaTiles = [
    { label: "Client (internal ref)", value: details.clientNameRef || "-" },
    { label: "Assigned To", value: details.assignedTo?.name || "Unassigned" },
    { label: "Requested By", value: details.createdByName || details.createdBy?.name || "-" },
    {
      label: "Delivery Date",
      value: details.deliveryDate ? moment(details.deliveryDate).format("MMM D, YYYY") : "-",
      alert: isOverdue,
    },
    { label: "Post Title", value: details.postTitle || "-" },
  ];
  const briefPanels = [
    { label: "Post Body", value: details.postBody },
    { label: "Graphic Brief", value: details.graphicBrief },
    { label: "Design Instructions", value: details.designInstructions },
  ].filter((p) => p.value);

  return (
    <>
      <div className="gfx-req-hero">
        <div className="gfx-req-hero-top">
          <div>
            <div className="gfx-req-hero-eyebrow">Graphics Request</div>
            <h2 className="gfx-req-hero-title">{details.campaignProject}</h2>
          </div>
          <div className="gfx-req-hero-actions">
            <span className="gfx-hero-pill">
              <span className={`gfx-priority-dot ${
                { Low: "priority-low", Medium: "priority-medium", High: "priority-high", Urgent: "priority-urgent" }[details.priority] || "priority-medium"
              }`} />
              {details.priority}
            </span>
            <span className="gfx-hero-pill gfx-hero-pill--status">{STAGE_LABEL[details.status] || details.status}</span>
            {details.status === "Waiting for Review" && (
              <>
                <Button className="gfx-hero-action-btn" loading={deciding} onClick={() => sendDecision("approved")}>
                  Approve
                </Button>
                <Button
                  className="gfx-hero-action-btn gfx-hero-action-btn--danger"
                  disabled={deciding}
                  onClick={() => setDecisionModal("revision_requested")}
                >
                  Request Revisions
                </Button>
              </>
            )}
            {details.status === "Completed" && (
              <Button
                className="gfx-hero-action-btn gfx-hero-action-btn--danger"
                disabled={deciding}
                onClick={() => setDecisionModal("revision_requested")}
              >
                Request Changes
              </Button>
            )}
            <Button className="gfx-hero-action-btn" icon={<EditOutlined />} onClick={openEdit}>
              Edit
            </Button>
            <Button className="gfx-hero-action-btn gfx-hero-action-btn--danger" icon={<DeleteOutlined />} onClick={deleteRequest}>
              Delete
            </Button>
          </div>
        </div>
      </div>

      <Card bordered={false} className="criclebox mb-24 gfx-req-info">
        <div className="gfx-meta-grid">
          {metaTiles.map((t) => (
            <div key={t.label} className={`gfx-meta-tile${t.alert ? " gfx-meta-tile--alert" : ""}`}>
              <div className="gfx-meta-label">{t.label}</div>
              <div className="gfx-meta-value">{t.value}</div>
            </div>
          ))}
        </div>

        {details.extensionRequest?.status === "pending" && (
          <div className="gfx-ext-banner">
            <div className="gfx-ext-banner-text">
              <b>Extension requested</b> to <b>{moment(details.extensionRequest.proposedDate).format("MMM D, YYYY")}</b>
              {details.extensionRequest.reason && (
                <div className="gfx-ext-banner-reason">"{details.extensionRequest.reason}"</div>
              )}
            </div>
            <div className="gfx-ext-banner-actions">
              <Button
                className="gfx-ext-btn gfx-ext-btn--approve"
                onClick={() => {
                  setExtensionDecisionModal("approved");
                  extensionDecisionForm.setFieldsValue({ approvedDate: moment(details.extensionRequest.proposedDate) });
                }}
              >
                Approve
              </Button>
              <Button className="gfx-ext-btn gfx-ext-btn--reject" onClick={() => setExtensionDecisionModal("rejected")}>
                Reject
              </Button>
              <Button className="gfx-ext-btn gfx-ext-btn--reassign" onClick={openEdit}>
                Reassign to Someone Else
              </Button>
            </div>
          </div>
        )}

        {briefPanels.length > 0 && (
          <div className="gfx-brief-stack">
            {briefPanels.map((p) => (
              <div key={p.label} className="gfx-brief-panel">
                <div className="gfx-brief-label">{p.label}</div>
                <p className="gfx-brief-text">{linkifyText(p.value)}</p>
              </div>
            ))}
          </div>
        )}

        {!!details.referenceFiles?.length && (
          <div className="gfx-ref-files">
            <div className="gfx-brief-label">Reference Files</div>
            <div className="gfx-ref-chips">
              {details.referenceFiles.map((f, i) => (
                <span key={i} className="gfx-ref-chip">
                  <FileOutlined />
                  <span className="gfx-ref-chip-name">{f.name}</span>
                  <a href={f.url} target="_blank" rel="noreferrer" title="View">
                    <EyeOutlined className="gfx-ref-chip-dl" />
                  </a>
                  <a href={f.url} onClick={(e) => { e.preventDefault(); downloadFile(f.url, f.name); }} title="Download">
                    <DownloadOutlined className="gfx-ref-chip-dl" />
                  </a>
                </span>
              ))}
            </div>
          </div>
        )}
      </Card>

      <Card bordered={false} className="criclebox">
        <Tabs
          className="gfx-tabs"
          items={[
            {
              key: "files",
              label: "Files & Versions",
              children: (
                <div className="gfx-version-list">
                  {versions.length === 0 ? (
                    <p className="gfx-brief-text">The Graphics Team member hasn't uploaded anything yet.</p>
                  ) : (
                    versions.map((v) => {
                      const isEditingNote = editingNoteId === v._id;
                      const isPendingDeletion = v.deletionRequest?.status === "pending";
                      return (
                        <div key={v._id || v.version} className="gfx-version-item">
                          <div
                            className="gfx-version-badge gfx-version-thumb"
                            onClick={() => setPreviewVersion(v)}
                            title="View"
                          >
                            {isImageFile(v.name) && !brokenThumbs[v._id] ? (
                              <img
                                src={v.url}
                                alt=""
                                className="gfx-version-thumb-img"
                                onError={() => setBrokenThumbs((prev) => ({ ...prev, [v._id]: true }))}
                              />
                            ) : (
                              <FileOutlined />
                            )}
                            <span className="gfx-version-thumb-tag">v{v.version}</span>
                          </div>
                          <div className="gfx-version-info">
                            <div className="gfx-version-name-row">
                              <span
                                className="gfx-version-name gfx-version-name--link"
                                onClick={() => setPreviewVersion(v)}
                                title="View"
                                role="button"
                              >
                                {v.name || `Version ${v.version}`}
                              </span>
                              <a className="gfx-version-inline-dl" href="#" onClick={(e) => { e.preventDefault(); setPreviewVersion(v); }} title="View">
                                <EyeOutlined />
                              </a>
                              <a className="gfx-version-inline-dl" href={v.url} onClick={(e) => { e.preventDefault(); downloadFile(v.url, v.name); }} title="Download">
                                <DownloadOutlined />
                              </a>
                            </div>
                            {!isEditingNote && (
                              <div className="gfx-version-meta">
                                Uploaded {moment(v.created_at).fromNow()}
                                {v.notes ? ` — ${v.notes}` : ""}
                              </div>
                            )}
                            {isPendingDeletion && (
                              <div className="gfx-version-delete-pending">
                                Graphics Team member requested to delete this file
                              </div>
                            )}
                            {isEditingNote && (
                              <div className="gfx-version-note-editor">
                                <Input
                                  autoFocus
                                  size="small"
                                  placeholder="Add a note about this file..."
                                  value={noteDraft}
                                  onChange={(e) => setNoteDraft(e.target.value)}
                                  onPressEnter={() => saveNote(v)}
                                />
                                <Button size="small" type="text" icon={<CheckOutlined />} loading={noteSaving} onClick={() => saveNote(v)} />
                                <Button size="small" type="text" icon={<CloseOutlined />} onClick={cancelEditingNote} />
                              </div>
                            )}
                          </div>
                          {!isEditingNote && isPendingDeletion && (
                            <div className="gfx-version-actions">
                              <Button
                                className="gfx-ext-btn gfx-ext-btn--approve"
                                loading={decidingDeletionId === v._id}
                                onClick={() => decideVersionDeletion(v, "approved")}
                              >
                                Approve
                              </Button>
                              <Button
                                className="gfx-ext-btn gfx-ext-btn--reject"
                                loading={decidingDeletionId === v._id}
                                onClick={() => decideVersionDeletion(v, "rejected")}
                              >
                                Reject
                              </Button>
                            </div>
                          )}
                          {!isEditingNote && !isPendingDeletion && (
                            <div className="gfx-version-actions">
                              <Button
                                className="gfx-version-icon-btn"
                                icon={<EditOutlined />}
                                title="Add / edit note"
                                onClick={() => startEditingNote(v)}
                              />
                              <Button
                                className="gfx-version-icon-btn gfx-version-icon-btn--danger"
                                icon={<DeleteOutlined />}
                                title="Delete version"
                                loading={deletingVersionId === v._id}
                                onClick={() => deleteVersion(v)}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              ),
            },
            {
              key: "discussion",
              label: "Discussion",
              children: (
                <>
                  <div className="gfx-comment-composer">
                    <TextArea
                      rows={3}
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Add a comment for the team..."
                    />
                    <Button className="gfx-comment-post-btn" loading={commentSaving} onClick={addComment}>
                      Post Comment
                    </Button>
                  </div>
                  <div className="gfx-comment-list">
                    {comments.length === 0 ? (
                      <p className="gfx-brief-text">No comments yet.</p>
                    ) : (
                      comments.map((c) => (
                        <div key={c._id} className="gfx-comment">
                          <div className="gfx-comment-avatar">
                            {(c.authorName || "T").trim().charAt(0).toUpperCase()}
                          </div>
                          <div className="gfx-comment-bubble">
                            <div className="gfx-comment-head">
                              <span className="gfx-comment-author">{c.authorName || "Team member"}</span>
                              <span className="gfx-comment-time">{moment(c.created_at).fromNow()}</span>
                            </div>
                            <p className="gfx-comment-text">{c.message}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              ),
            },
            {
              key: "audit",
              label: "Audit Trail",
              children: (
                <div className="gfx-audit">
                  <Timeline>
                    {auditLog.map((a) => (
                      <Timeline.Item key={a._id}>
                        <span className="gfx-audit-action">{a.action.replace(/_/g, " ")}</span>
                        {" by "}
                        <span className="gfx-audit-actor">{a.actorName || "system"}</span>
                        <div className="gfx-audit-time">
                          {moment(a.created_at).format("MMM D, YYYY hh:mm a")}
                        </div>
                        {a.comment && <div className="gfx-audit-comment">{a.comment}</div>}
                      </Timeline.Item>
                    ))}
                  </Timeline>
                </div>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        open={!!decisionModal}
        title={details.status === "Completed" ? "Reopen Request" : "Request Revisions"}
        onCancel={() => setDecisionModal(null)}
        onOk={() => decisionForm.submit()}
        okText={details.status === "Completed" ? "Reopen" : "Request Revisions"}
        confirmLoading={deciding}
      >
        <Form form={decisionForm} layout="vertical" onFinish={submitDecision}>
          <Form.Item label="Comments" name="comment">
            <TextArea
              rows={3}
              placeholder={
                details.status === "Completed"
                  ? "What needs to change? This reopens the request and notifies the Graphics Team member."
                  : "Add feedback for the Graphics Team member..."
              }
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={editVisible}
        title="Edit Graphics Request"
        width={720}
        onCancel={() => setEditVisible(false)}
        okText="Save Changes"
        okButtonProps={{ loading: editSaving }}
        onOk={() => editForm.submit()}
      >
        {details.status === "Completed" && (
          <Alert
            type="warning"
            showIcon
            message="This will reopen the request"
            description="It's currently Completed — saving changes here sends it back to the Graphics Team member as an active request. Reassigning to a different member instead resets it to a fresh start."
            style={{ marginBottom: 16 }}
          />
        )}
        <Form form={editForm} layout="vertical" onFinish={submitEdit}>
          <Row gutter={20}>
            <Col span={12}>
              <Form.Item label="Graphic Brief" name="graphicBrief">
                <TextArea rows={3} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Design Instructions" name="designInstructions">
                <TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={20}>
            <Col span={12}>
              <Form.Item label="Delivery Date" name="deliveryDate" rules={[{ required: true, message: "Required" }]}>
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Priority" name="priority">
                <Select options={["Low", "Medium", "High", "Urgent"].map((p) => ({ label: p, value: p }))} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Assign To" name="assignedTo">
            <Select
              allowClear
              placeholder="Graphics Team member"
              options={members.map((m) => ({ label: m.name, value: m._id }))}
            />
          </Form.Item>
          {assignedToWatch !== editingOriginalAssignee && (
            <Form.Item
              label="Reason for Reassignment"
              name="reassignReason"
              rules={[{ required: true, message: "Please add a short reason — it will be shown in the audit log" }]}
            >
              <Input placeholder="e.g. Original assignee is on leave" />
            </Form.Item>
          )}
          <Form.Item label="Add Reference Files (optional)">
            <Upload
              multiple
              beforeUpload={(file) => {
                setEditFileList((prev) => [...prev, file]);
                return false;
              }}
              onRemove={(file) => setEditFileList((prev) => prev.filter((f) => f.uid !== file.uid))}
              fileList={editFileList}
            >
              <Button icon={<UploadOutlined />}>Select Files</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={!!extensionDecisionModal}
        title={extensionDecisionModal === "approved" ? "Approve Extension" : "Reject Extension"}
        onCancel={() => setExtensionDecisionModal(null)}
        onOk={() => extensionDecisionForm.submit()}
        okText={extensionDecisionModal === "approved" ? "Approve" : "Reject"}
        okButtonProps={{ danger: extensionDecisionModal === "rejected" }}
        confirmLoading={extensionDeciding}
      >
        <Form form={extensionDecisionForm} layout="vertical" onFinish={submitExtensionDecision}>
          {extensionDecisionModal === "approved" && (
            <Form.Item
              label="Delivery Date"
              name="approvedDate"
              rules={[{ required: true, message: "Required" }]}
              extra="Pre-filled with the Graphics Team member's proposed date — change it to approve a different date instead."
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          )}
          <Form.Item label="Note (optional)" name="note">
            <TextArea rows={2} placeholder="Add a note for the Graphics Team member..." />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={!!previewVersion}
        title={previewVersion?.name || (previewVersion ? `Version ${previewVersion.version}` : "")}
        onCancel={() => setPreviewVersion(null)}
        footer={[
          <Button key="download" icon={<DownloadOutlined />} onClick={() => downloadFile(previewVersion.url, previewVersion.name)}>
            Download
          </Button>,
          <Button key="close" type="primary" onClick={() => setPreviewVersion(null)}>
            Close
          </Button>,
        ]}
        width={800}
        className="gfx-preview-modal"
      >
        {previewVersion && isImageFile(previewVersion.name) && (
          <img src={previewVersion.url} alt={previewVersion.name} className="gfx-preview-image" />
        )}
        {previewVersion && isPdfFile(previewVersion.name) && (
          <iframe src={previewVersion.url} title={previewVersion.name} className="gfx-preview-frame" />
        )}
        {previewVersion && !isImageFile(previewVersion.name) && !isPdfFile(previewVersion.name) && (
          <div className="gfx-preview-unsupported">
            <FileOutlined style={{ fontSize: 40 }} />
            <p>Preview isn't available for this file type. Download it to view.</p>
          </div>
        )}
      </Modal>
    </>
  );
}

export default GraphicsRequestDetail;
